import { assertUnreachable } from "common/utils/assertUnreachable";
import { parseHatchPattern } from "geom/hatch";
import hatchMaterialLibrary from "geom/hatchLibrary";
import type { HatchMaterialId } from "geom/hatchMaterialId";
import type { HatchMaterial, HatchPatternMaterial, HatchProceduralMaterial } from "geom/hatchTypes";
import { HatchMaterialType, hatchPatternMaterialNew, hatchProceduralMaterialNew } from "geom/hatchTypes";
import THREEAssetLoader from "./THREEAssetLoader";

export type HatchMaterialCache = { [key: string]: Promise<HatchMaterial> };

const MIN_HATCH_PATTERN_SCALE = 0.01;
const defaultHatchPatternParams = Object.freeze({
  scale: 1,
  offsetX: 0,
  offsetY: 0,
  angle: 0,
});

function emptyHatchPattern(): HatchPatternMaterial {
  return hatchPatternMaterialNew([]);
}

async function getHatchPatternSource(url: string): Promise<string | null> {
  if (!url) {
    return null;
  }

  try {
    const res = await THREEAssetLoader.instance.fetchJson(url);

    if (!res) {
      throw new Error("Error getting hatch pattern");
    }

    return res;
  } catch (e) {
    console.error("Could not find hatch pattern", url, e);
    return "";
  }
}

export function getCachedLibraryHatchMaterial(
  hatchMaterialId: HatchMaterialId,
  cache: HatchMaterialCache,
): Promise<HatchMaterial> {
  const loc = hatchMaterialLibrary[hatchMaterialId];

  if (!loc) {
    console.error("Unknown hatch material id: " + hatchMaterialId);
    return Promise.resolve(emptyHatchPattern());
  }

  if (hatchMaterialId in cache) {
    return cache[hatchMaterialId];
  }

  return (cache[hatchMaterialId] = new Promise<HatchProceduralMaterial | HatchPatternMaterial>((resolve) => {
    void (async () => {
      try {
        let hatchMaterial;
        switch (loc.type) {
          case HatchMaterialType.PATTERN: {
            const url = loc.hatchMaterialJSON;
            const res = await THREEAssetLoader.instance.fetchJson(url);

            if (!res) {
              throw new Error("Error getting hatch material");
            }

            const params = JSON.parse(res);

            const finalParams = {
              ...defaultHatchPatternParams,
              ...params,
            };

            finalParams.scale = Math.max(finalParams.scale, MIN_HATCH_PATTERN_SCALE);

            const { hatchPatternURL, scale, offsetX, offsetY, angle } = finalParams;

            if (hatchPatternURL) {
              const source = await getHatchPatternSource(hatchPatternURL);
              if (source) {
                hatchMaterial = hatchPatternMaterialNew(parseHatchPattern(source, scale, offsetX, offsetY, angle));
              }
            }

            break;
          }
          case HatchMaterialType.PROCEDURAL: {
            // procedural hatch materials are passed nearly unmodified along with the id
            // this allows the system to later lookup the function from hatchMaterialLibrary
            hatchMaterial = hatchProceduralMaterialNew(hatchMaterialId);
            break;
          }
          default: {
            assertUnreachable(loc);
          }
        }
        resolve(hatchMaterial);
      } catch (e) {
        console.error("Could not make hatch material", hatchMaterialId, loc, e);
        resolve(emptyHatchPattern());
      }
    })();
  }));
}

export function getLibraryHatchMaterialSize(hatchMaterialId: HatchMaterialId): { sizeU: number; sizeV: number } {
  const DEFAULT_SIZE = 1;
  const materialSrc = hatchMaterialLibrary[hatchMaterialId];
  if (materialSrc.type === HatchMaterialType.PROCEDURAL) {
    return materialSrc;
  } else {
    // Pattern materials are difficult to size correctly.
    return { sizeU: DEFAULT_SIZE, sizeV: DEFAULT_SIZE };
  }
}
